import { forwardRef, useCallback, useContext, ElementRef, ChangeEvent } from "react";
import ReactInputMask from "react-input-mask";

import { FormControlContext } from "../../FormControl/FormControlContext";
import { TextInput, TextInputProps } from "../TextInput";
import { MaskUtils } from "~/application/utils";

export type PhoneInputProps = TextInputProps;

export const PhoneInput = forwardRef<ElementRef<typeof ReactInputMask>, PhoneInputProps>(
  (
    {
      onFocus,
      name: nameProps,
      disabled: disabledProps,
      onBlur: onBlurProps,
      onChange: onChangeProps,
      value: valueProps,
      ...props
    },
    forwardedRef
  ) => {
    const formControl = useContext(FormControlContext);

    const name = nameProps ?? formControl.name;
    const disabled = disabledProps ?? formControl.disabled;
    const onBlur = onBlurProps ?? formControl.onBlur;
    const onChange = onChangeProps ?? formControl.onChange;
    const value = valueProps ?? formControl.value;
    const isDirty = !!formControl.error;

    const handlePaste = useCallback((event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      
      const pastedText = event.clipboardData.getData('text');
      const target = event.target as HTMLInputElement;
      const currentValue = target.value;
      const selectionStart = target.selectionStart || 0;
      const selectionEnd = target.selectionEnd || 0;
      
      const newValue = currentValue.slice(0, selectionStart) + pastedText + currentValue.slice(selectionEnd);
      
      const formattedValue = MaskUtils.asPhoneNumber(newValue);
      
      onChange?.({
        ...event,
        target: {
          ...target,
          value: formattedValue
        },
      } as any);
    }, [onChange, value]);

    const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      const formattedValue = MaskUtils.asPhoneNumber(newValue);

      onChange?.({
        ...event,
        target: {
          ...event.target,
          value: formattedValue
        },
      });
    }, [onChange, value]);

    const CustomInput = useCallback(
      (inputProps: TextInputProps) => <TextInput disabled={disabled} {...props} {...inputProps} />,
      [props, disabled]
    );

    return (
      <ReactInputMask
        alwaysShowMask={false}
        mask={MaskUtils.pickPhoneNumberMask(value || "")}
        maskChar=""
        onChange={handleChange}
        onPaste={handlePaste}
        onFocus={onFocus}
        onBlur={onBlur}
        value={value}
        disabled={disabled}
        name={name}
        data-dirty={isDirty}
        ref={forwardedRef}
      >
        {CustomInput}
      </ReactInputMask>
    );
  }
);

PhoneInput.displayName = "PhoneInput";